import { AnonymousSession } from './domain';

export interface AnonymousSessionStorage {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
}

export interface ResolveAnonymousSessionOptions {
    storageKey?: string;
    storage?: AnonymousSessionStorage | null;
    now?: Date | string;
    createId?: () => string;
    expiresAt?: string | null;
}

export const anonymousSessionStorageKey = 'lookit.anonymous-session.v1';
export const anonymousUserIdPrefix = 'anonymous:';

function browserStorage(storage?: AnonymousSessionStorage | null): AnonymousSessionStorage | null {
    if (storage !== undefined) return storage;
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
}

function nowIso(now?: Date | string): string {
    if (typeof now === 'string') return now;
    return (now || new Date()).toISOString();
}

function randomIdPart(): string {
    const globalCrypto = typeof globalThis !== 'undefined' ? globalThis.crypto : null;
    if (globalCrypto && typeof globalCrypto.randomUUID === 'function') {
        return globalCrypto.randomUUID().replace(/-/g, '');
    }
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
}

export function createAnonymousSessionId(): string {
    return `anon_${randomIdPart().slice(0, 32)}`;
}

export function anonymousSessionUserId(anonymousSessionId: string): string {
    return `${anonymousUserIdPrefix}${anonymousSessionId}`;
}

export function anonymousSessionIdFromUserId(userId?: string | null): string | null {
    if (!userId || !userId.startsWith(anonymousUserIdPrefix)) return null;
    const sessionId = userId.slice(anonymousUserIdPrefix.length);
    return sessionId || null;
}

function parseStoredSession(rawValue: string | null): AnonymousSession | null {
    if (!rawValue) return null;

    try {
        const parsed = JSON.parse(rawValue) as Partial<AnonymousSession>;
        if (!parsed || typeof parsed.anonymous_session_id !== 'string') return null;
        return {
            anonymous_session_id: parsed.anonymous_session_id,
            auth_provider: 'anonymous',
            user_id: null,
            created_at: parsed.created_at || nowIso(),
            last_seen_at: parsed.last_seen_at || parsed.created_at || nowIso(),
            expires_at: parsed.expires_at || null
        };
    } catch (error) {
        return null;
    }
}

export function resolveAnonymousSession(options: ResolveAnonymousSessionOptions = {}): AnonymousSession {
    const storageKey = options.storageKey || anonymousSessionStorageKey;
    const resolvedStorage = browserStorage(options.storage);
    const timestamp = nowIso(options.now);
    const storedSession = parseStoredSession(resolvedStorage?.getItem(storageKey) || null);
    const session: AnonymousSession = storedSession
        ? { ...storedSession, last_seen_at: timestamp }
        : {
            anonymous_session_id: options.createId ? options.createId() : createAnonymousSessionId(),
            auth_provider: 'anonymous',
            user_id: null,
            created_at: timestamp,
            last_seen_at: timestamp,
            expires_at: options.expiresAt || null
        };

    if (resolvedStorage) {
        resolvedStorage.setItem(storageKey, JSON.stringify(session));
    }

    return session;
}

export const anonymousSessionService = {
    storageKey: anonymousSessionStorageKey,
    userIdPrefix: anonymousUserIdPrefix,
    createId: createAnonymousSessionId,
    resolve: resolveAnonymousSession,
    userId: anonymousSessionUserId,
    sessionIdFromUserId: anonymousSessionIdFromUserId
};
